import * as React from 'react';
import Timeago from 'react-timeago';
import { Title } from '../../utils/title';
import { useThemeSwitcher } from 'react-css-theme-switcher';
import { useInterval } from 'usehooks-ts';
import { TileLayout } from '@progress/kendo-react-layout';
import { ConversionPercentageResponse } from './ConversionPercentage';
import { fetchApi } from 'services/api';
import { formatNumber } from '@progress/kendo-intl';
import { useEffect } from 'react';
import { TripCountByServiceTeamResponse } from './TripCountByServiceTeam';
import { getServiceTeamName } from 'utils/utils';
import { RadialGauge } from '@progress/kendo-react-gauges';
import { useIsMobile } from 'utils/useIsMobile';
import { formatter } from '.';
import { UserQuoteCountResponse } from './UserQuoteCount';
import { BookCountResponse } from './UserBookCount';
import { DailyGoalRevenueResponse, DayOfWeekColors } from './DailyGoalRevenue';

type DispatchCountResponse = Array<{ FullName: string, Count: number }>;

const TVReports = () => {
  const { status } = useThemeSwitcher();
  const isMobile = useIsMobile();
  const [lastRefresh, setLastRefresh] = React.useState(new Date());
  const [tripCount, setTripCount] = React.useState<number>(0);
  const [tripCountByServiceTeam, setTripCountByServiceTeam] = React.useState<TripCountByServiceTeamResponse>([]);
  const [conversionPercentage, setConversionPercentage] = React.useState<number>(0);
  const [overallMargin, setOverallMargin] = React.useState<number>(0);
  const [dispatchCount, setDispatchCount] = React.useState<DispatchCountResponse>([]);
  const [quoteCount, setQuoteCount] = React.useState<UserQuoteCountResponse>([]);
  const [bookCount, setBookCount] = React.useState<BookCountResponse>([]);
  const [dailyGoals, setDailyGoals] = React.useState<DailyGoalRevenueResponse>([]);

  const refresh = () => {
     fetchApi(`/api/Report/TripCountByServiceTeam/DTW*`)
      .then((response: TripCountByServiceTeamResponse) => {
        setTripCount(response.map(x => x.Count).reduce((a, b) => a + b, 0));
        setTripCountByServiceTeam(response.sort((a, b) => b.Count - a.Count));
      });

    fetchApi(`/api/Report/ConversionPercentage/DTW*`)
      .then((response: ConversionPercentageResponse) => {
        setConversionPercentage(response.find(x => x.TimePeriod === 'Today').ConversionPercentage);
      });

    fetchApi(`/api/Report/OverallMarginPercentageToday/DTW*`)
      .then((response: number) => {
        setOverallMargin(response);
      });

    fetchApi(`/api/Report/BookCountToday/DTW*`)
      .then((response: BookCountResponse) => {
        setBookCount(response);
      });

    fetchApi(`/api/Report/DispatchCountToday/DTW*`)
      .then((response: DispatchCountResponse) => {
        setDispatchCount(response);
      });

    fetchApi(`/api/Report/QuoteCountToday/DTW*`)
      .then((response: UserQuoteCountResponse) => {
        setQuoteCount(response);
      });

    fetchApi(`/api/Report/DailyGoals`)
      .then((response: DailyGoalRevenueResponse) => {
        setDailyGoals(response);
      });

    setLastRefresh(new Date());
  }

  useEffect(() => {
    refresh();
  }, []);

  useInterval(() => {
    refresh();
  }, 5 * 60 * 1000);

  if (status !== 'loaded') {
    return <div>Loading...</div>;
  }

  const weekToDateRevenue = dailyGoals.slice(0, dailyGoals.findIndex(g => g.Today) + 1).reduce((sum, goal) => sum + goal.Actual, 0);
  const weeklyTarget = dailyGoals.reduce((sum, goal) => sum + goal.Goal, 0);

  const tiles = [
    {
      defaultPosition: {
        col: 1,
        row: 1,
        colSpan: 1,
        rowSpan: 1,
      },
      header: 'DTW Daily Trip Count',
      body: <div className="gauge-div">
        <RadialGauge
          transitions={false}
          pointer={{ value: tripCount }}
          scale={{
            max: Math.max(700, tripCount),
            labels: {
              format: "{0}",
            },
            ranges: [
              { from: 0, to: 400, color: "#dc3545" },
              { from: 400, to: 500, color: "#ffc107" },
              { from: 500, to: Math.max(700, tripCount), color: "#198754" }
            ]
          }}
        />
        <p className="dashboard-card-content-number">{formatNumber(tripCount, 'n')}</p>
      </div>,
    },
    {
      defaultPosition: {
        col: isMobile ? 1 : 2,
        row: isMobile ? 2 : 1,
        colSpan: 1,
        rowSpan: 1,
      },
      header: 'DTW Secure Rate',
      body: <div className="dashboard-card-content">
        <RadialGauge
          transitions={false}
          pointer={{ value: conversionPercentage }}
          scale={{
            max: .5,
            labels: {
              format: "{0:p}",
            },
            ranges: [
              { from: 0, to: .20, color: "#dc3545" },
              { from: .20, to: .35, color: "#ffc107" },
              { from: .35, to: .5, color: "#198754" }
            ]
          }}
        />
        <p className="dashboard-card-content-number">{formatNumber(conversionPercentage, 'p')}</p>
      </div>,
    },
    {
      defaultPosition: {
        col: isMobile ? 1 : 3,
        row: isMobile ? 3 : 1,
        colSpan: 1,
        rowSpan: 1,
      },
      header: 'DTW Brokerage Margin',
      body: <div className="dashboard-card-content">
        <RadialGauge
          transitions={false}
          pointer={{ value: overallMargin }}
          scale={{
            max: .5,
            labels: {
              format: "{0:p}",
            },
            ranges: [
              { from: 0, to: .10, color: "#dc3545" },
              { from: .10, to: .20, color: "#ffc107" },
              { from: .20, to: .5, color: "#198754" }
            ]
          }}
        />
        <p className="dashboard-card-content-number">{formatNumber(overallMargin, 'p')}</p>
      </div>,
    },
    {
      defaultPosition: {
        col: isMobile ? 1 : 4,
        row: isMobile ? 4 : 1,
        colSpan: 1,
        rowSpan: 1,
      },
      header: 'Daily Goal and Week Rev.',
      body: dailyGoals.filter(goal => goal.Today).map((goal, index) => (
        <div key={index} className="dashboard-card-content">
          <RadialGauge
            transitions={false}
            pointer={{ value: weekToDateRevenue / 1000000 }}
            scale={{
              max: Math.max(weeklyTarget, weekToDateRevenue) / 1000000,
              labels: {
                format: "{0:c1}m",
              },
              ranges: dailyGoals.reduce((ranges, goal, index) => {
                const from = ranges.length > 0 ? ranges[ranges.length - 1].to : 0;
                const to = from + goal.Goal / 1000000;
                ranges.push({ from, to, color: DayOfWeekColors[index] });
                return ranges;
              }, []),
            }}
          />
          <p className="text-center mb-0" style={{ fontSize: '2.8rem' }}>
            {formatNumber(goal.Goal / 1000, 'c0')}k Goal<br />
            {formatNumber(goal.Actual / 1000, 'c0')}k Actual
          </p>
        </div>
      ))
    }
  ];

  const tables = [
    {
      defaultPosition: {
        col: 1,
        row: 1,
        colSpan: 1,
        rowSpan: 1,
      },
      header: 'DTW Trip Count by Service Team',
      body: <div className="dashboard-card-content" style={{ fontSize: 26 }}>
         <table style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Service Team</th>
              <th style={{ textAlign: 'right' }}>Count</th>
            </tr>
          </thead>
          <tbody>
            {tripCountByServiceTeam.map((serviceTeam, index) => {
              return <tr key={index}>
                <td>{getServiceTeamName(serviceTeam.ServiceTeamID)}</td>
                <td style={{ textAlign: 'right' }}>{serviceTeam.Count}</td>
              </tr>
            })}
          </tbody>
        </table>
      </div>,
    },
    {
      defaultPosition: {
        col: isMobile ? 1 : 2,
        row: isMobile ? 2 : 1,
        colSpan: 1,
        rowSpan: 1,
      },
      header: 'DTW Book Count Today',
      body: <div className="dashboard-card-content" style={{ fontSize: 26 }}>
        <table style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Booking User</th>
              <th style={{ textAlign: 'right' }}>Count</th>
            </tr>
          </thead>
          <tbody>
            {bookCount.map((book, index) => {
              return <tr key={index}>
                <td>{book.FullName}</td>
                <td style={{ textAlign: 'right' }}>{book.Count}</td>
              </tr>
            })}
          </tbody>
        </table>
      </div>,
    },
    {
      defaultPosition: {
        col: isMobile ? 1 : 3,
        row: isMobile ? 3 : 1,
        colSpan: 1,
        rowSpan: 1,
      },
      header: 'DTW Dispatch Count Today',
      body: <div className="dashboard-card-content" style={{ fontSize: 26 }}>
        <table style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Dispatcher</th>
              <th style={{ textAlign: 'right' }}>Count</th>
            </tr>
          </thead>
          <tbody>
            {dispatchCount.map((dispatch, index) => {
              return <tr key={index}>
                <td>{dispatch.FullName}</td>
                <td style={{ textAlign: 'right' }}>{dispatch.Count}</td>
              </tr>
            })}
          </tbody>
        </table>
      </div>,
    },
    {
      defaultPosition: {
        col: isMobile ? 1 : 4,
        row: isMobile ? 4 : 1,
        colSpan: 1,
        rowSpan: 1,
      },
      header: 'DTW Quoted Today',
      body: <div className="dashboard-card-content" style={{ fontSize: 26 }}>
        <table style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>By</th>
              <th style={{ textAlign: 'right' }}>Count</th>
            </tr>
          </thead>
          <tbody>
            {quoteCount.map((quote, index) => {
              return <tr key={index}>
                <td>{quote.FullName}</td>
                <td style={{ textAlign: 'right' }}>{formatNumber(quote.Count, 'n')}</td>
              </tr>
            })}
          </tbody>
        </table>
      </div>,
    }
  ];

  return <React.Fragment>
      <Title string="TV Dashboard" />
      <TileLayout
        columns={isMobile ? 1 : 4}
        items={tiles}
      />
      <TileLayout
        columns={isMobile ? 1 : 4}
        items={tables}
      />

      <div className="text-muted text-center my-2">Last Refresh: <Timeago formatter={formatter} date={lastRefresh} /></div>
    </React.Fragment>
}

export default TVReports;
